import apiClient from './client.js'

/**
 * データセット管理 API クライアント
 */
export const datasetApi = {
  /**
   * プラットフォーム管理者が公開データセットを作成・割り当て
   * @param {Object} data - { name, description?, price_per_search, assigned_user_id?, provider_user_id? }
   * @returns {Promise<Object>}
   */
  async assignDataset(data) {
    const response = await apiClient.post('/console/v1/admin/datasets/assign', data)
    return response.data
  },

  /**
   * データプロバイダーが公開データセットを作成
   * @param {Object} data - { dataset_id?, name, description?, price_per_search }
   * @returns {Promise<Object>}
   */
  async createDataset(data) {
    const response = await apiClient.post('/console/v1/datasets/public/create', data)
    return response.data
  },

  /**
   * 公開データセット一覧を取得
   * @param {Object} params - { provider_user_id?, is_free? }
   * @returns {Promise<Object>}
   */
  async listDatasets(params = {}) {
    const response = await apiClient.get('/console/v1/datasets/public', { params })
    return response.data
  },

  /**
   * データセット詳細を取得
   * @param {string} datasetId
   * @returns {Promise<Object>}
   */
  async getDataset(datasetId) {
    const response = await apiClient.get(`/console/v1/datasets/${datasetId}`)
    return response.data
  },

  /**
   * データセット価格を変更
   * @param {string} datasetId
   * @param {Object} data - { new_price, reason? }
   * @returns {Promise<Object>}
   */
  async changePrice(datasetId, data) {
    const response = await apiClient.post(`/console/v1/datasets/${datasetId}/price`, data)
    return response.data
  },

  /**
   * データセットを削除（非公開化）
   * @param {string} datasetId
   * @returns {Promise<Object>}
   */
  async deleteDataset(datasetId) {
    const response = await apiClient.delete(`/console/v1/datasets/${datasetId}`)
    return response.data
  },
}

/**
 * 収益レポート API クライアント
 */
export const revenueApi = {
  /**
   * データプロバイダーの月次収益を取得
   * @param {string} yearMonth - YYYY-MM形式
   * @returns {Promise<Object>}
   */
  async getProviderMonthlyRevenue(yearMonth) {
    const response = await apiClient.get('/console/v1/datasets/revenue/monthly', {
      params: { year_month: yearMonth },
    })
    return response.data
  },

  /**
   * 特定データセットの月次収益を取得
   * @param {string} datasetId
   * @param {string} yearMonth - YYYY-MM形式
   * @returns {Promise<Object>}
   */
  async getDatasetMonthlyRevenue(datasetId, yearMonth) {
    const response = await apiClient.get(`/console/v1/datasets/revenue/dataset/${datasetId}`, {
      params: { year_month: yearMonth },
    })
    return response.data
  },

  /**
   * プラットフォーム全体の月次収益サマリーを取得（管理者のみ）
   * @param {string} yearMonth - YYYY-MM形式
   * @returns {Promise<Object>}
   */
  async getPlatformRevenueSummary(yearMonth) {
    const response = await apiClient.get('/console/v1/admin/datasets/revenue/platform', {
      params: { year_month: yearMonth },
    })
    return response.data
  },
}

/**
 * POI一括インポート API クライアント
 */
export const poiApi = {
  /**
   * POI一括インポート
   * @param {string} datasetId
   * @param {File} file
   * @param {string} format - 'csv' | 'json'
   * @returns {Promise<Object>}
   */
  async bulkImport(datasetId, file, format) {
    const formData = new FormData()
    formData.append('dataset_id', datasetId)
    formData.append('file', file)
    formData.append('format', format)

    const response = await apiClient.post('/console/v1/poi/bulk-import', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    return response.data
  },

  /**
   * POI検索
   * @param {Object} params - { dataset_id, latitude, longitude, radius?, limit? }
   * @returns {Promise<Object>}
   */
  async search(params) {
    const response = await apiClient.get('/api/v1/poi/search', { params })
    return response.data
  },
}
